import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BaseQueryBuilderPayload,
  BodyPrimary,
  CheckBox,
  Dropdown,
  QueryBuilder,
  QueryBuilderProps,
  QueryBuilderRoot,
  StyledModal,
  Tag,
} from "@bikdotai/bik-component-library";
import { SingleOption } from "@bikdotai/bik-component-library/dist/esm/components/dropdown/type";
import { SegmentValidator } from "../../app/action-block/filter/validator";
import {
  extractHeaderAndSubHeader,
  sanitiseState,
} from "../../app/action-block/filter/utils";
import {
  segmentFilterApiHandler,
  userPropertyFilterAPIHandler,
} from "../../app/action-block/filter/api";
import { CustomFieldHelper } from "../../app/action-block/filter/helpers/customFieldHelper";
import { clearSharedQueryBuilderCache } from "../../app/action-block/filter/helpers/queryBuilderCache";
import { useAppSelector } from "../../state/store";
import { WarnIcon } from "../../icons/warnIcon";
import ChannelAccountDropdown, {
  useFlowChannelAccounts,
} from "./channelAccountDropdown";
import { BuilderContainer, ExcludeToggleRow, TriggerOnRow } from "./styled";

export enum SegmentEvent {
  ENTERED = "enteredASegment",
  EXITED = "exitedASegment",
}

// Read by the chatbot backend's relay trigger parser; the campaign segment
// state carries it along untouched.
export const EVENT_SELECTED_META_KEY = "event_selected";

export const SEGMENT_TRIGGER_HEADERS: Record<SegmentEvent, string> = {
  [SegmentEvent.ENTERED]: "Entered segment",
  [SegmentEvent.EXITED]: "Exited segment",
};

export const isSegmentEntryExitTrigger = (trigger?: { header?: string }) =>
  Object.values(SEGMENT_TRIGGER_HEADERS).includes(trigger?.header ?? "");

const TRIGGER_ON_OPTIONS: SingleOption[] = [
  {
    label: SEGMENT_TRIGGER_HEADERS[SegmentEvent.ENTERED],
    value: SegmentEvent.ENTERED,
  },
  {
    label: SEGMENT_TRIGGER_HEADERS[SegmentEvent.EXITED],
    value: SegmentEvent.EXITED,
  },
];

// Only date custom fields are listed, so the placeholder names them.
const PROPERTY_PLACEHOLDERS = {
  HAS_CUSTOM_FIELD: "Select custom date",
};

const INCLUDE_SELECTOR_KEY = "eventTrigger";
const EXCLUDE_SELECTOR_KEY = "eventTriggerExclude";

const segmentValidator = new SegmentValidator();
const customFieldHelper = new CustomFieldHelper();

interface SegmentEntryExitProps {
  storeId: string;
  mode: QueryBuilderProps["mode"];
  visible: boolean;
  channel: string;
  currentChannelId?: string;
  segmentId?: string;
  includeState?: QueryBuilderRoot;
  excludeState?: QueryBuilderRoot;
  onSave: (data: any) => void;
  onClose: () => void;
}

const readEventFromNodes = (nodes?: any[]): SegmentEvent | undefined => {
  const value = nodes?.[0]?.metas?.find(
    (meta: any) => meta.key === EVENT_SELECTED_META_KEY
  )?.value;
  return Object.values(SegmentEvent).includes(value) ? value : undefined;
};

// The event rides on the first include node, after that node's own metas so
// the library's segment picker still finds its segmentID at index 0.
const withEventMeta = (nodes: any[], event: SegmentEvent) =>
  nodes.map((node, index) => {
    const metas = (node.metas ?? []).filter(
      (meta: any) => meta.key !== EVENT_SELECTED_META_KEY
    );
    if (index !== 0) return { ...node, metas };
    return {
      ...node,
      metas: [...metas, { key: EVENT_SELECTED_META_KEY, value: event }],
    };
  });

const flattenSegments = (data: any): { id: string; name: string }[] =>
  Array.isArray(data) ? data : (Object.values(data ?? {}).flat() as any[]);

const SegmentEntryExit: React.FC<SegmentEntryExitProps> = (props) => {
  const { storeId, channel } = props;
  const includeRef = useRef<QueryBuilderRoot | undefined>(props.includeState);
  const excludeRef = useRef<QueryBuilderRoot | undefined>(props.excludeState);
  const segmentsRef = useRef<{ id: string; name: string }[]>([]);
  const [event, setEvent] = useState<SegmentEvent | undefined>(
    readEventFromNodes(props.includeState?.nodes)
  );
  const [excludeEnabled, setExcludeEnabled] = useState<boolean>(
    !!props.excludeState?.nodes?.length
  );
  const [includeErrors, setIncludeErrors] = useState<QueryBuilderRoot>();
  const [excludeErrors, setExcludeErrors] = useState<QueryBuilderRoot>();
  const [errorText, setErrorText] = useState<string>("");
  const customerCustomFields = useAppSelector(
    (state) => state.customFieldsState.customerCustomFields
  );
  const channelAccounts = useFlowChannelAccounts(
    channel,
    props.currentChannelId
  );

  const dateCustomFields = useMemo(
    () =>
      customFieldHelper.getDateFieldsForRelativeTrigger(customerCustomFields),
    [customerCustomFields]
  );

  useEffect(() => {
    clearSharedQueryBuilderCache();
  }, []);

  const typesAPIHandler = useMemo(
    () => ({
      SEGMENT_FILTER: async (payload: BaseQueryBuilderPayload<undefined>) => {
        const response = await segmentFilterApiHandler(payload, storeId);
        if ("data" in response && response.data) {
          segmentsRef.current = flattenSegments(response.data);
        }
        return response;
      },
      USER_PROPERTY_FILTER: (
        payload: BaseQueryBuilderPayload<
          undefined | { propertyName: string; searchKey?: string }
        >
      ) => userPropertyFilterAPIHandler(payload, storeId),
      HAS_CUSTOM_FIELD: async (
        payload: BaseQueryBuilderPayload<{
          propertyName?: string;
          searchKey?: string;
        }>
      ) => ({
        data: customFieldHelper.hasCustomFieldPropertyHandler(
          dateCustomFields,
          payload
        ),
      }),
    }),
    [storeId, dateCustomFields]
  );

  const validate = () => {
    const [hasErr, state] = segmentValidator.validate({
      include: includeRef.current as QueryBuilderRoot,
      exclude: excludeEnabled ? excludeRef.current : undefined,
    });
    setIncludeErrors({ ...state.include });
    setExcludeErrors(state.exclude ? { ...state.exclude } : undefined);
    return { hasErr, state };
  };

  const buildSubHeader = (frontendState: QueryBuilderRoot) => {
    const first: any = frontendState.nodes[0];
    let text = "";
    if (first.type === "SEGMENT_FILTER") {
      const segmentId = first.metas?.find(
        (meta: any) => meta.key === "segmentID"
      )?.value;
      const name =
        segmentsRef.current.find((segment) => segment.id === segmentId)?.name ??
        segmentId;
      text = `Part of segment ${name ?? ""}`.trim();
    } else {
      text = extractHeaderAndSubHeader(
        frontendState,
        dateCustomFields
      ).subHeader.trim();
    }
    const more = frontendState.nodes.length - 1;
    return more > 0 ? `${text} +${more} more` : text;
  };

  const save = () => {
    if (!channelAccounts.selected?.value) {
      setErrorText("Please select an account");
      return;
    }
    if (!event) {
      setErrorText("Please select entered or exited segment");
      return;
    }
    const { hasErr, state } = validate();
    if (hasErr) {
      setErrorText("Some inputs are missing or incorrect. Please check.");
      return;
    }

    const include = sanitiseState(state.include);
    const exclude =
      excludeEnabled && state.exclude?.nodes?.length
        ? sanitiseState(state.exclude)
        : undefined;
    const frontendNodes = withEventMeta(include.frontendState.nodes, event);

    props.onSave({
      nodes: withEventMeta(include.backendState.nodes, event),
      operator: include.backendState.operator,
      exclude: exclude
        ? {
            nodes: exclude.backendState.nodes,
            operator: exclude.backendState.operator,
          }
        : undefined,
      frontendNodes,
      frontendOperator: include.frontendState.operator,
      frontendExclude: exclude ? exclude.frontendState : undefined,
      header: SEGMENT_TRIGGER_HEADERS[event],
      subHeader: buildSubHeader(include.frontendState),
      id: frontendNodes[0]._id,
      type: "relay",
      segmentId: props.segmentId,
      channelId: channelAccounts.selected.value,
    });
    props.onClose();
  };

  return (
    <StyledModal
      onClose={props.onClose}
      zIndex={200}
      open={props.visible}
      headerRightCustomElement={
        <ChannelAccountDropdown
          channel={channel}
          options={channelAccounts.options}
          selected={channelAccounts.selected}
          isLoading={channelAccounts.isLoading}
          select={channelAccounts.select}
        />
      }
      headingTitle="Create trigger based on Segment entry and exit"
      centralContainerStyles={{ width: "80%", minHeight: "80%" }}
      footerLeftCustomElement={
        !!errorText && (
          <Tag tagText={errorText} type="negative" LeadingIcon={WarnIcon} />
        )
      }
      primaryButton={{
        buttonType: "primary",
        buttonText: "Create",
        onClick: save,
      }}
    >
      <BuilderContainer style={{ padding: 24 }}>
        <TriggerOnRow>
          <BodyPrimary>Trigger on</BodyPrimary>
          <Dropdown
            size="small"
            width="218px"
            placeHolder="Entered/exited segment"
            value={event ? undefined : ""}
            options={TRIGGER_ON_OPTIONS.map((option) => ({
              ...option,
              selected: option.value === event,
            }))}
            onSelect={(option) => {
              setEvent((option as SingleOption).value as SegmentEvent);
              setErrorText("");
            }}
          />
        </TriggerOnRow>

        <QueryBuilder
          mode={props.mode}
          state={
            props.includeState?.nodes?.length ? props.includeState : undefined
          }
          addNodeBtnText="Add filter"
          queryPreText="Customers who are in"
          selectorKey={INCLUDE_SELECTOR_KEY}
          onStateChange={(state) => {
            includeRef.current = state;
          }}
          typesAPIHandlers={
            typesAPIHandler as QueryBuilderProps["typesAPIHandlers"]
          }
          propertyPlaceholders={PROPERTY_PLACEHOLDERS}
          infinite={true}
          errorState={includeErrors}
        />

        <ExcludeToggleRow>
          <CheckBox
            isChecked={excludeEnabled}
            onValueChange={() => setExcludeEnabled(!excludeEnabled)}
          />
          <BodyPrimary>Exclude customers with certain property</BodyPrimary>
        </ExcludeToggleRow>

        {excludeEnabled && (
          <QueryBuilder
            mode={props.mode}
            state={
              props.excludeState?.nodes?.length ? props.excludeState : undefined
            }
            addNodeBtnText="Add filter"
            queryPreText="Exclude customers who"
            selectorKey={EXCLUDE_SELECTOR_KEY}
            onStateChange={(state) => {
              excludeRef.current = state;
            }}
            typesAPIHandlers={
              typesAPIHandler as QueryBuilderProps["typesAPIHandlers"]
            }
            propertyPlaceholders={PROPERTY_PLACEHOLDERS}
            infinite={true}
            errorState={excludeErrors}
          />
        )}
      </BuilderContainer>
    </StyledModal>
  );
};

export default SegmentEntryExit;
